import React from 'react';
import { observer } from 'mobx-react';

import { Popover } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { UuidableName } from 'utils/api/types';

import EntitySearchField from 'components/EntitySearchField/EntitySearchField';

import { EntityModules } from 'core/constants';

import DateRangePickerPopover, { formatDateRange, DateRangeValue } from '../DateRangePickerPopover';

import { StyledButton, IconWrapper, PopoverContent, FieldContainer, FieldLabel, StartDateButton } from './styles';

export interface MoreFiltersValue {
  site: UuidableName | null;
  asset: UuidableName | null;
  contact: UuidableName | null;
  startDate: DateRangeValue;
}

const MoreFiltersPicker = ({
  value,
  onChange,
}: {
  value: MoreFiltersValue;
  onChange: (value: MoreFiltersValue) => void;
}): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const calendarOpen = Boolean(calendarAnchorEl);

  const hasActiveFilters =
    value.site !== null ||
    value.asset !== null ||
    value.contact !== null ||
    value.startDate.from !== null ||
    value.startDate.to !== null;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleSiteChange = (_field: string, site: UuidableName | UuidableName[] | null): void => {
    onChange({ ...value, site: site as UuidableName | null });
  };

  const handleAssetChange = (_field: string, asset: UuidableName | UuidableName[] | null): void => {
    onChange({ ...value, asset: asset as UuidableName | null });
  };

  const handleContactChange = (_field: string, contact: UuidableName | UuidableName[] | null): void => {
    onChange({ ...value, contact: contact as UuidableName | null });
  };

  const handleCalendarOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = (): void => {
    setCalendarAnchorEl(null);
  };

  const handleStartDateChange = (newValue: DateRangeValue): void => {
    onChange({ ...value, startDate: newValue });
  };

  const startDateDisplayValue = formatDateRange(value.startDate);

  return (
    <>
      <StyledButton onClick={handleOpen} $isActive={hasActiveFilters}>
        <IconWrapper $isActive={hasActiveFilters}>
          <TuneIcon fontSize="small" />
        </IconWrapper>
        More filters
      </StyledButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          <FieldContainer>
            <FieldLabel>Site</FieldLabel>
            <EntitySearchField
              field="site"
              updateField={handleSiteChange}
              data={value.site}
              module={EntityModules.Sites}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Asset</FieldLabel>
            <EntitySearchField
              field="asset"
              updateField={handleAssetChange}
              data={value.asset}
              module={EntityModules.Assets}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Contact</FieldLabel>
            <EntitySearchField
              field="contact"
              updateField={handleContactChange}
              data={value.contact}
              module={EntityModules.Contacts}
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Start date</FieldLabel>
            <StartDateButton onClick={handleCalendarOpen} $hasValue={Boolean(startDateDisplayValue)} $size="medium">
              {startDateDisplayValue || 'Select date range'}
            </StartDateButton>
          </FieldContainer>
        </PopoverContent>
      </Popover>

      <DateRangePickerPopover
        open={calendarOpen}
        anchorEl={calendarAnchorEl}
        value={value.startDate}
        onClose={handleCalendarClose}
        onChange={handleStartDateChange}
      />
    </>
  );
};

export default observer(MoreFiltersPicker);
